import { keyframes } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

/**
 * Single full clockwise revolution used by the Retry icon during the
 * retry-in-flight window. Mirrors the rotation contract on the
 * Figma annotation node `15612:63903` ("Animate retry icon (rotate
 * clockwise motion)") and the `keyframes` import pattern used by the
 * shared bd-ui `BDSpinner` so this animation participates in the
 * theme-aware Emotion cache instead of leaking a raw global rule.
 */
const rotateClockwise = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const useStyles = makeStyles()((theme) => ({
  /**
   * Bare container — no surface, border, or padding. The agent error
   * sits in-flow as if it were an assistant reply (Figma node
   * `15703:85631`). Vertical stack of two regions:
   *
   * 1. `responseGroup` — message + Retry button (4px internal gap,
   *    mirrors the Figma `y=26` offset against the 22px message text).
   * 2. `MessageFeedbackRow` — thumbs / overflow / freeform feedback,
   *    pinned 12px below the Retry button per Figma node
   *    `15591:62326` (`y=60` from Figma minus the 48px responseGroup
   *    height).
   */
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0.75rem',
  },
  /**
   * Message + Retry button group. Internal 4px gap matches the
   * Figma `y=26` Retry offset against the 22px message line.
   */
  responseGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0.25rem',
  },
  /**
   * Plain assistant-reply-style body text. `Typography` already
   * applies `small_body_regular`; we only reset the default `<p>`
   * margins so the 4px gap from the parent is the only spacing
   * between the message and the Retry button.
   */
  message: {
    margin: 0,
    color: theme.new.color.textPrimary,
  },
  /**
   * Pixel-fidelity overrides for the bd-ui Button so it matches the
   * Figma `Link-Button/Web` component (node `15703:85571` for the
   * default state, `15703:85612` for the disabled-while-retrying
   * state at node `15612:63773`):
   *
   * - `fontSize: 0.875rem` (14px) + `lineHeight: 1.6` override the
   *   shared `body_medium` typography (16px / 1.5) that the bd-ui
   *   Button inherits from the theme.
   * - `textTransform: 'none'` is belt-and-suspenders against any
   *   downstream surface that re-introduces the MUI default
   *   uppercase.
   * - The nested `MuiButton-endIcon` rule pins the 4px text→icon
   *   gap declared on the Figma frame; the icon controls its own
   *   12×12 footprint via the SVG `width`/`height` props.
   * - The disabled-state color is already correct out of the box:
   *   bd-ui's `tertiaryStyles` `&.Mui-disabled` rule applies
   *   `theme.new.color.grey[400]` (`#B0B9C4`) to both `color` and
   *   `stroke` (via the `stroke-icon` class), which is the exact
   *   `content/tertiary` token the Figma disabled retry uses.
   */
  retryButton: {
    fontSize: '0.875rem',
    lineHeight: 1.6,
    textTransform: 'none',
    '& .MuiButton-endIcon': {
      marginLeft: '4px',
      marginRight: 0,
    },
  },
  /**
   * Applied to the Retry button only while a retry is in flight
   * (`isRetrying === true`). Spins the embedded refresh-cw-05 glyph
   * one revolution per second to satisfy the Figma annotation node
   * `15612:63903` for the click state demonstrated in frame
   * `15612:63773`. Respects `prefers-reduced-motion` so users who
   * have opted out of motion still get the grey disabled treatment
   * without the spinning glyph.
   */
  retryButtonRetrying: {
    '& .MuiButton-endIcon svg': {
      animation: `${rotateClockwise} 1s linear infinite`,
      transformOrigin: 'center',
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
      },
    },
  },
}));

export default useStyles;
